import { Card, CardContent, Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import { CustomTooltip, Section } from "components/disw";
import React from "react";
import DetailCard from "./DetailCard";
import PropertyRow from "./PropertyRow";
import { formatUserName, User } from "./userName";
import { sortVersionsDescending } from "./versions";

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    padding: theme.spacing(2),
    boxShadow: "0px 1px 2px 0px #0000004D",
    display: "flex",
    flexDirection: "column",
    "& > :last-child": {
      paddingBottom: "0px !important"
    }
  },
  listCard: {
    width: 400,
    cursor: "pointer"
  },
  cardContent: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3)
  },
  label: {
    fontFamily: "Segoe UI",
    fontWeight: 700,
    fontSize: "14.4px",
    lineHeight: "19px",
    letterSpacing: 0,
    color: "#1E1E1E",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    marginBottom: 0
  }
}));

export interface DataSourceSummary {
  key: string;
  creationDate: Date;
  creator: User;
  access: string[];
  allVersions: { version: string }[];
}

interface DataSourceItemProps {
  dataSource: DataSourceSummary;
  onClick?: (key: string) => void;
  detailedView?: boolean;
  children?: React.ReactNode;
}

const DataSourceItem: React.FC<DataSourceItemProps> = ({
  dataSource,
  onClick,
  detailedView = false,
  children
}) => {
  const classes = useStyles();
  const latestVersion = sortVersionsDescending(dataSource.allVersions)[0];

  const infoRows = (
    <Grid container columnSpacing={2} rowSpacing={0.5}>
      <PropertyRow name="Latest version">
        {latestVersion?.version ?? "-"}
      </PropertyRow>
      {!detailedView && (
        <>
          <PropertyRow name="Versions">
            {dataSource.allVersions.length}
          </PropertyRow>
          <PropertyRow name="API keys">{dataSource.access.length}</PropertyRow>
        </>
      )}
      <PropertyRow name="Created at">
        <DateComponent
          date={dataSource.creationDate}
          variant={DateVariant.SHORT_WITH_TIME}
        />
      </PropertyRow>
      <PropertyRow name="Created by">
        {formatUserName(dataSource.creator)}
      </PropertyRow>
    </Grid>
  );

  return detailedView ? (
    <DetailCard
      title={dataSource.key}
      data-testid={`data-source-${dataSource.key}`}
    >
      <Section name="Details">{infoRows}</Section>
      {children}
    </DetailCard>
  ) : (
    <Card
      variant="outlined"
      className={clsx(classes.card, classes.listCard)}
      onClick={onClick ? () => onClick(dataSource.key) : undefined}
      data-testid={`data-source-${dataSource.key}`}
    >
      <CardContent className={classes.cardContent}>
        <CustomTooltip title={dataSource.key}>
          <Typography component="div" className={classes.label}>
            {dataSource.key}
          </Typography>
        </CustomTooltip>
        {infoRows}
      </CardContent>
    </Card>
  );
};

export default DataSourceItem;
